import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import config from "../../config";
import dayjs from "dayjs";
import {
  Row,
  Col,
  Button,
  DatePicker,
  Form,
  Skeleton,
  Alert,
} from "antd";
import { NavLink } from "react-router-dom";
import { PrinterOutlined } from "@ant-design/icons";
import LoginChecked from "../Shared/LoginChecked";
import { IdentificationIcon } from "@heroicons/react/24/outline";
import { NumberFormat } from "../Shared/Utility";

function parseAmountString(value) {
  if (value === null || value === undefined || value === "") return null;
  const s = String(value).replace(/,/g, "").trim();
  const n = parseFloat(s, 10);
  return Number.isNaN(n) ? null : n;
}

function formatNumericField(value, fractionDigits) {
  const n = parseAmountString(value);
  if (n === null) return value === undefined || value === "" ? "—" : String(value);
  return NumberFormat(n, fractionDigits);
}

/** Ant Design RangePicker may set dayjs or (if mis-bound) a string — always format safely. */
function formatRangeDay(d) {
  if (!d) return null;
  if (typeof d.format === "function") return d.format("YYYY-MM-DD");
  return dayjs(d).isValid() ? dayjs(d).format("YYYY-MM-DD") : null;
}

function SummarySheet() {
  const AccessKey = localStorage.getItem("AccessKey");
  const UserID = localStorage.getItem("ID");
  const [loading, setLoading] = useState(false);
  const [StartDate, SetStartDate] = useState(() => dayjs().subtract(1, "month"));
  const [EndDate, SetEndDate] = useState(() => dayjs());
  const [summaryData, setSummaryData] = useState({});
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    document.title = "Summary Sheet";

    if (!StartDate || !EndDate || !UserID || !AccessKey) {
      setSummaryData({});
      setLoadError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError(null);

    const data = {
      UserID: String(UserID),
      AccessKey: AccessKey,
      StartDate: formatRangeDay(StartDate),
      EndDate: formatRangeDay(EndDate),
    };

    const api_config = {
      method: "post",
      url: config.base_url + "Report/GetSummarySheet",
      headers: {
        "Content-Type": "application/json",
      },
      data: data,
    };

    axios(api_config)
      .then(function (response) {
        if (response.data.status_code === 1) {
          setSummaryData(response.data);
          setLoadError(null);
        } else {
          setSummaryData({});
          setLoadError(
            response.data.status_message || "Unable to load the summary sheet."
          );
        }
        setLoading(false);
      })
      .catch(function (error) {
        console.log(error);
        setSummaryData({});
        setLoadError("Network error. Please try again.");
        setLoading(false);
      });
  }, [EndDate, StartDate, UserID, AccessKey]);

  const { RangePicker } = DatePicker;

  // onChange(dates, dateStrings) — first arg is [Dayjs, Dayjs]; second is string tuple (not Dayjs).
  const handleDateChange = useCallback((dates) => {
    if (!dates || !dates[0] || !dates[1]) {
      SetStartDate(null);
      SetEndDate(null);
      return;
    }
    SetStartDate(dates[0]);
    SetEndDate(dates[1]);
  }, []);

  const disabledDate = (current) => {
    return current && current > dayjs().endOf("day");
  };

  const printQuery =
    StartDate && EndDate && formatRangeDay(StartDate) && formatRangeDay(EndDate)
      ? `?StartDate=${encodeURIComponent(
          formatRangeDay(StartDate)
        )}&EndDate=${encodeURIComponent(formatRangeDay(EndDate))}`
      : "";

  return (
    <>
      <LoginChecked />
      <div className="right-side-contents">
        <div className="page-content">
          <div className="page-header">
            <h3 className="page-title">Summary Sheet</h3>

            <div className="header-actions">
              <NavLink
                to={`/summary-report/print-list${printQuery}`}
                target="_blank"
                style={{
                  pointerEvents: StartDate && EndDate ? "auto" : "none",
                  opacity: StartDate && EndDate ? 1 : 0.5,
                }}
              >
                <Button
                  type="dashed"
                  shape="round"
                  size="large"
                  icon={<PrinterOutlined />}
                  disabled={!StartDate || !EndDate}
                >
                  Print Report
                </Button>
              </NavLink>
            </div>
          </div>

          <p style={{ marginBottom: 16, maxWidth: 800, color: "#595959" }}>
            Books start from <strong>1 Jan 2022</strong> (system go-live). The
            server uses that date as the default range start when no start date is
            sent. <strong>Expense total</strong> is the{" "}
            <em>net balance of expense accounts at the end date</em>, not only
            expenses posted within the selected period.
          </p>

          <div className="filter-wrap">
            <Form layout="vertical">
              <Form.Item label="Period">
                <RangePicker
                  allowClear
                  value={
                    StartDate && EndDate ? [StartDate, EndDate] : null
                  }
                  onChange={handleDateChange}
                  disabledDate={disabledDate}
                />
              </Form.Item>
            </Form>
          </div>

          {loadError ? (
            <Alert type="error" message={loadError} showIcon style={{ marginBottom: 16 }} />
          ) : null}

          {loading ? (
            <Row>
              <Col xs={24} md={16}>
                <Skeleton active />
                <Skeleton active />
              </Col>
            </Row>
          ) : !StartDate || !EndDate ? (
            <Alert
              type="info"
              message="Select a date range to load the summary sheet."
              showIcon
            />
          ) : (
            <div style={{ margin: "10px 0px 50px 0px" }}>
              <Row justify={"center"}>
                <Col md={20} sm={24}>
                  <div className="fb-card">
                    <div className="fb-card-header">
                      <div className="fb-card-title">
                        <span>
                          <IdentificationIcon />
                        </span>
                        <h4>SUMMARY SHEET DETAILS</h4>
                      </div>
                    </div>

                    <div
                      className="fb-card-body table "
                      style={{ "--cardheight": "470px" }}
                    >
                      <ul className="profit-list">
                        <li>
                          <h4>Opening cash:</h4>
                          <p>
                            {summaryData.OpeningCash
                              ? summaryData.OpeningCash
                              : "—"}
                          </p>
                        </li>
                        <li>
                          <h4>Opening cash at banks:</h4>
                          <p>
                            {summaryData.OpeningCashatBanks
                              ? summaryData.OpeningCashatBanks
                              : "—"}
                          </p>
                        </li>
                        <li>
                          <h4>Cash sales:</h4>
                          <p>
                            {summaryData.CashSales
                              ? formatNumericField(summaryData.CashSales, 2)
                              : "—"}
                          </p>
                        </li>
                        <li>
                          <h4>Credit sales:</h4>
                          <p>
                            {summaryData.CreditSales
                              ? formatNumericField(summaryData.CreditSales, 2)
                              : "—"}
                          </p>
                        </li>
                        <li>
                          <h4>Cash purchase:</h4>
                          <p>
                            {summaryData.CashPurchase
                              ? formatNumericField(summaryData.CashPurchase, 2)
                              : "—"}
                          </p>
                        </li>
                        <li>
                          <h4>Credit purchase:</h4>
                          <p>
                            {summaryData.CreditPurchase
                              ? formatNumericField(
                                  summaryData.CreditPurchase,
                                  2
                                )
                              : "—"}
                          </p>
                        </li>
                        <li>
                          <h4>Total received (cash vouchers):</h4>
                          <p>
                            {summaryData.TotalReceived
                              ? formatNumericField(summaryData.TotalReceived, 0)
                              : "—"}
                          </p>
                        </li>
                        <li>
                          <h4>Total payments (cash out, excl. expense/discount):</h4>
                          <p>
                            {summaryData.TotalPayments
                              ? formatNumericField(summaryData.TotalPayments, 0)
                              : "—"}
                          </p>
                        </li>
                        <li>
                          <h4>Expense accounts (balance at period end):</h4>
                          <p>
                            {summaryData.TotalExpenses
                              ? formatNumericField(summaryData.TotalExpenses, 0)
                              : "—"}
                          </p>
                        </li>
                        <li>
                          <h4>Total discounts (vouchers):</h4>
                          <p>
                            {summaryData.TotalDiscounts
                              ? formatNumericField(
                                  summaryData.TotalDiscounts,
                                  0
                                )
                              : "—"}
                          </p>
                        </li>
                        <li>
                          <h4>Closing cash:</h4>
                          <p>
                            {summaryData.ClosingCash
                              ? summaryData.ClosingCash
                              : "—"}
                          </p>
                        </li>
                        <li>
                          <h4>Closing cash at banks:</h4>
                          <p>
                            {summaryData.ClosingCashatBanks
                              ? summaryData.ClosingCashatBanks
                              : "—"}
                          </p>
                        </li>
                      </ul>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default SummarySheet;
